import React, { useEffect, useState } from 'react';
import './ManagerClockTimecard.css';
import managerClockService from './DoctorSummaryService';
import DoctorInfoService from '../doctorInfo/doctorInfoService';
import OfficeService from '../office/officeService';

const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// 🔹 Haversine distance in meters
const deg2rad = (deg) => (deg * Math.PI) / 180;
const distanceMeters = (lat1, lon1, lat2, lon2) => {
  const R = 6371000; // Earth radius in meters
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) *
      Math.cos(deg2rad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// 🔹 Color class based on distance
const getDistanceClass = (meters) => {
  if (meters == null) return 'distance-none';
  if (meters <= 20) return 'distance-green';
  if (meters <= 50) return 'distance-yellow';
  return 'distance-red';
};

const formatDistanceLabel = (meters) => {
  if (meters == null) return 'No location recorded';
  return `${meters.toFixed(1)} m`;
};

// 🔹 Normalize office address for Nominatim
const normalizeAddress = (raw) => {
  if (!raw) return '';

  let s = raw;

  s = s.replace(/,\s*/g, ', ');
  s = s.replace(/\b(Ste|STE|Suite|SUITE)\s*\w+/g, '');
  s = s.replace(/#\s*\w+/g, '');
  s = s.replace(/\s{2,}/g, ' ');
  s = s.replace(/\s*,\s*$/, '').trim();

  return s;
};

// 🔹 For datetime-local input (local timezone)
const toDatetimeLocal = (isoString) => {
  if (!isoString) return '';
  const d = new Date(isoString);
  const tzOffset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - tzOffset * 60000);
  return local.toISOString().slice(0, 16); // YYYY-MM-DDTHH:mm
};

const ManagerClockTimecard = () => {
  const [clockData, setClockData] = useState([]);
  const [expandedDoctorId, setExpandedDoctorId] = useState(null);
  const [expandedCell, setExpandedCell] = useState({ doctorId: null, day: null });
  const [weekStart, setWeekStart] = useState(() => {
    const now = new Date();
    const monday = new Date(now.setDate(now.getDate() - now.getDay() + 1));
    return monday.toISOString().split('T')[0];
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [doctorNames, setDoctorNames] = useState({});
  const [offices, setOffices] = useState([]);
  const [officeCoords, setOfficeCoords] = useState({});
  const [loadingOffices, setLoadingOffices] = useState(false);

  // 🔹 Editing state (only one cell at a time)
  const [editing, setEditing] = useState(null);
  // editing = { doctorId, day, logId, clockIn, clockOut }
  const [savingEdit, setSavingEdit] = useState(false);

  // ===== Fetch weekly clock data =====
  const fetchClockData = async () => {
    try {
      const token = localStorage.getItem('jwt');

      if (!token) {
        console.error('No JWT token found in localStorage');
        setError('You are not logged in. Please log in again.');
        setClockData([]);
        return;
      }

      const response = await fetch(`/api/doctor-clock/week-summary?weekStart=${weekStart}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        console.error('Error fetching clock summary:', response.status, response.statusText);
        setError(
          response.status === 401
            ? 'Unauthorized (401). Please log in again or check your permissions.'
            : `Failed to load data (status ${response.status}).`
        );
        setClockData([]);
        return;
      }

      const data = await response.json();

      if (!Array.isArray(data)) {
        console.error('Unexpected response shape for week-summary:', data);
        setError('Unexpected data format from server.');
        setClockData([]);
        return;
      }

      setError(null);
      setSuccess('');
      setClockData(data);
    } catch (err) {
      console.error('Failed to fetch clock summary:', err);
      setError('Failed to load data. Please try again later.');
      setClockData([]);
    }
  };

  useEffect(() => {
    fetchClockData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart]);

  // ===== Load real doctor names =====
  useEffect(() => {
    const loadNames = async () => {
      if (!clockData.length) return;

      const service = new DoctorInfoService();
      const ids = [...new Set(clockData.map((d) => d.doctorId).filter(Boolean))];

      const updates = {};
      for (const id of ids) {
        if (doctorNames[id]) continue;
        try {
          const doc = await service.getDoctorById(id);
          updates[id] =
            `${doc.firstname || ''} ${doc.lastname || ''}`.trim() || `Doctor ${id}`;
        } catch (err) {
          console.error(`Failed to load doctor ${id}:`, err);
          updates[id] = `Doctor ${id}`;
        }
      }

      if (Object.keys(updates).length > 0) {
        setDoctorNames((prev) => ({ ...prev, ...updates }));
      }
    };

    loadNames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clockData]);

  // ===== Load offices & geocode addresses =====
  useEffect(() => {
    const loadOfficesAndCoords = async () => {
      setLoadingOffices(true);
      try {
        const officeService = new OfficeService();
        const all = (await officeService.getAllOffices()) || [];
        setOffices(all);

        const coordsMap = {};

        for (const office of all) {
          if (!office.address) continue;

          const cleaned = normalizeAddress(office.address);

          try {
            const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(
              cleaned
            )}`;
            const res = await fetch(url);
            const json = await res.json();

            if (Array.isArray(json) && json[0]) {
              coordsMap[office.officeId] = {
                lat: parseFloat(json[0].lat),
                lng: parseFloat(json[0].lon),
                name: office.officeName,
                address: office.address,
              };
            } else {
              console.warn(
                'No geocode result for',
                office.officeName,
                'raw:',
                office.address,
                'cleaned:',
                cleaned
              );
            }
          } catch (e) {
            console.error('Geocoding failed for office', office.officeName, e);
          }
        }

        setOfficeCoords(coordsMap);
      } catch (e) {
        console.error('Failed to load offices / coordinates', e);
      } finally {
        setLoadingOffices(false);
      }
    };

    loadOfficesAndCoords();
  }, []);

  // ===== Find nearest office for a given coordinate =====
  const getNearestOffice = (lat, lng) => {
    if (
      lat == null ||
      lng == null ||
      !officeCoords ||
      Object.keys(officeCoords).length === 0
    ) {
      return null;
    }

    let best = null;
    for (const [officeId, oc] of Object.entries(officeCoords)) {
      if (oc.lat == null || oc.lng == null) continue;
      const d = distanceMeters(lat, lng, oc.lat, oc.lng);
      if (!best || d < best.distance) {
        best = {
          officeId: Number(officeId),
          distance: d,
          office: oc,
        };
      }
    }
    return best;
  };

  const toggleExpand = (doctorId) => {
    setExpandedDoctorId((prev) => (prev === doctorId ? null : doctorId));
  };

  const toggleDayDetails = (doctorId, day, hasMinutes) => {
    if (!hasMinutes) return;
    setExpandedCell((prev) =>
      prev.doctorId === doctorId && prev.day === day
        ? { doctorId: null, day: null }
        : { doctorId, day }
    );
  };

  const handleCSVExport = async () => {
    try {
      await managerClockService.exportCSV(weekStart);
    } catch (err) {
      console.error('CSV export failed:', err);
      alert('Failed to export CSV');
    }
  };

  const changeWeek = (days) => {
    const current = new Date(weekStart);
    current.setDate(current.getDate() + days);
    setWeekStart(current.toISOString().split('T')[0]);
    setEditing(null);
    setExpandedCell({ doctorId: null, day: null });
    setExpandedDoctorId(null);
    setSuccess('');
    setError(null);
  };

  const calculateDailyTotals = () => {
    const totals = {};
    for (const doctor of clockData || []) {
      const weeklyLog = doctor.weeklyLog || {};
      for (const [day, log] of Object.entries(weeklyLog)) {
        const minutes = (log && log.totalMinutes) || 0;
        totals[day] = (totals[day] || 0) + minutes;
      }
    }
    return totals;
  };

  const calculateWeeklyTotal = (doctor) => {
    const weeklyLog = doctor.weeklyLog || {};
    return Object.values(weeklyLog).reduce(
      (sum, log) => sum + ((log && log.totalMinutes) || 0),
      0
    );
  };

  const dailyTotals = calculateDailyTotals();

  const formatTime = (value) => {
    if (!value) return 'N/A';
    try {
      return new Date(value).toLocaleTimeString();
    } catch {
      return String(value);
    }
  };

  // ===== Editing handlers =====

  const startEdit = async (e, doctorId, day, log) => {
    e.stopPropagation();
    setError(null);
    setSuccess('');

    // 1) If backend already puts `logId` in weeklyLog, use it
    if (log && log.logId) {
      setEditing({
        doctorId,
        day,
        logId: log.logId,
        clockIn: toDatetimeLocal(log.clockIn),
        clockOut: toDatetimeLocal(log.clockOut),
      });
      return;
    }

    // 2) Otherwise, fetch raw logs for this doctor and match by date in the current week
    try {
      const token = localStorage.getItem('jwt');
      if (!token) {
        alert('Not logged in. Please log in again.');
        return;
      }

      const res = await fetch(`/api/doctor-clock/logs/${doctorId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Failed to load logs for doctor ${doctorId}`);
      }

      const allLogs = await res.json(); // list of DoctorClockLog

      // Calculate the actual date for this column
      const dayIndex = daysOfWeek.indexOf(day); // 0–6
      const base = new Date(weekStart);
      const targetDateObj = new Date(base);
      targetDateObj.setDate(base.getDate() + dayIndex);
      const targetDateStr = targetDateObj.toISOString().split('T')[0]; // YYYY-MM-DD

      // Try to find a log with that date
      const matching = allLogs.find((l) => {
        if (l.date) {
          // If backend has a `date` field
          return l.date === targetDateStr;
        }
        if (l.clockIn) {
          const d = new Date(l.clockIn);
          const dStr = d.toISOString().split('T')[0];
          return dStr === targetDateStr;
        }
        return false;
      });

      if (!matching) {
        alert('No editable log found for that day.');
        return;
      }

      setEditing({
        doctorId,
        day,
        logId: matching.id,
        clockIn: toDatetimeLocal(matching.clockIn),
        clockOut: toDatetimeLocal(matching.clockOut),
      });
    } catch (err) {
      console.error('Failed to resolve log to edit:', err);
      alert('Failed to find the underlying log for that day.');
    }
  };

  const cancelEdit = (e) => {
    if (e) e.stopPropagation();
    setEditing(null);
  };

  const saveEdit = async (e) => {
    e.stopPropagation();
    if (!editing || !editing.logId) return;

    setSavingEdit(true);
    setError(null);
    setSuccess('');
    try {
      const token = localStorage.getItem('jwt');
      const body = {};

      if (editing.clockIn) body.clockIn = editing.clockIn + ':00';
      if (editing.clockOut) body.clockOut = editing.clockOut + ':00';

      const response = await fetch(`/api/doctor-clock/edit-log/${editing.logId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      const text = await response.text();

      if (!response.ok) {
        throw new Error(text || 'Failed to update log');
      }

      setSuccess('Log updated successfully.');
      setEditing(null);
      await fetchClockData();
    } catch (err) {
      console.error('Failed to save edited log:', err);
      setError(err.message || 'Failed to save edited log.');
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <div className="timecard-container">
      <h1>Weekly Timecard Summary</h1>

      {/* 🔹 Office coordinates summary at the top */}
      <div className="office-coords-summary">
        <h2>Office Locations (OpenStreetMap)</h2>
        {loadingOffices && <p>Loading office coordinates…</p>}
        {!loadingOffices && offices.length === 0 && (
          <p>No offices found. Please check office configuration.</p>
        )}
        {!loadingOffices && offices.length > 0 && (
          <table className="office-coords-table">
            <thead>
              <tr>
                <th>Office</th>
                <th>Address</th>
                <th>Latitude</th>
                <th>Longitude</th>
              </tr>
            </thead>
            <tbody>
              {offices.map((office) => {
                const oc = officeCoords[office.officeId];
                return (
                  <tr key={office.officeId}>
                    <td>{office.officeName}</td>
                    <td>{office.address}</td>
                    <td>{oc && oc.lat != null ? oc.lat.toFixed(6) : 'N/A'}</td>
                    <td>{oc && oc.lng != null ? oc.lng.toFixed(6) : 'N/A'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <p className="legend">
          <strong>Legend: </strong>
          <span className="badge distance-green">0–20m (On-site)</span>{' '}
          <span className="badge distance-yellow">20–50m (Nearby)</span>{' '}
          <span className="badge distance-red">50m+ (Off-site)</span>
        </p>
      </div>

      <div className="top-controls">
        <div className="week-nav">
          <button onClick={() => changeWeek(-7)} className="week-btn">
            ⬅ Previous Week
          </button>
          <label>
            Start of Week:
            <input
              type="date"
              value={weekStart}
              onChange={(e) => setWeekStart(e.target.value)}
            />
          </label>
          <button onClick={() => changeWeek(7)} className="week-btn">
            Next Week ➡
          </button>
        </div>

        <button className="export-btn" onClick={handleCSVExport}>
          Export CSV
        </button>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {success && <div className="success-banner">{success}</div>}

      <div className="timecard-table">
        <div className="timecard-header sticky">
          <div className="header-cell doctor-name">Doctor</div>
          {daysOfWeek.map((day) => (
            <div key={day} className="header-cell">
              {day}
            </div>
          ))}
          <div className="header-cell">Total</div>
        </div>

        {(clockData || []).map((doctor) => {
          const weeklyLog = doctor.weeklyLog || {};
          const weeklyTotalMinutes = calculateWeeklyTotal(doctor);

          const displayName =
            doctorNames[doctor.doctorId] || doctor.name || `Doctor ${doctor.doctorId}`;

          return (
            <React.Fragment key={doctor.doctorId}>
              <div className="timecard-row">
                <div
                  className="row-cell doctor-name clickable"
                  onClick={() => toggleExpand(doctor.doctorId)}
                  title="Click to expand all days for this doctor"
                >
                  {displayName}
                </div>

                {daysOfWeek.map((day) => {
                  const log = weeklyLog[day] || {};
                  const minutes = log.totalMinutes || 0;
                  const isExpanded =
                    expandedCell.doctorId === doctor.doctorId &&
                    expandedCell.day === day;

                  const nearest =
                    log.clockInLat != null && log.clockInLng != null
                      ? getNearestOffice(log.clockInLat, log.clockInLng)
                      : null;
                  const distance = nearest ? nearest.distance : null;
                  const distanceClass = getDistanceClass(distance);

                  const isEditingCell =
                    editing &&
                    editing.doctorId === doctor.doctorId &&
                    editing.day === day;

                  if (isEditingCell) {
                    return (
                      <div
                        key={day}
                        className={`row-cell time worked selected-slot ${distanceClass}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="edit-field">
                          <span>Clock In:</span>
                          <input
                            type="datetime-local"
                            className="edit-input"
                            value={editing.clockIn || ''}
                            onChange={(e) =>
                              setEditing((prev) =>
                                prev ? { ...prev, clockIn: e.target.value } : prev
                              )
                            }
                            disabled={savingEdit}
                          />
                        </div>
                        <div className="edit-field">
                          <span>Clock Out:</span>
                          <input
                            type="datetime-local"
                            className="edit-input"
                            value={editing.clockOut || ''}
                            onChange={(e) =>
                              setEditing((prev) =>
                                prev ? { ...prev, clockOut: e.target.value } : prev
                              )
                            }
                            disabled={savingEdit}
                          />
                        </div>
                        <div className="edit-buttons">
                          <button
                            className="save-btn"
                            onClick={saveEdit}
                            disabled={savingEdit}
                          >
                            Save
                          </button>
                          <button
                            className="cancel-btn"
                            onClick={cancelEdit}
                            disabled={savingEdit}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={day}
                      className={`row-cell time ${
                        minutes > 0 ? 'worked clickable' : ''
                      } ${isExpanded ? 'selected-slot' : ''} ${distanceClass}`}
                      onClick={() =>
                        !editing && toggleDayDetails(doctor.doctorId, day, minutes > 0)
                      }
                      title={
                        minutes > 0
                          ? nearest
                            ? `${formatDistanceLabel(distance)} from ${
                                nearest.office?.name || 'nearest office'
                              }`
                            : 'No location data for this day'
                          : ''
                      }
                    >
                      {minutes
                        ? `${Math.floor(minutes / 60)}h ${minutes % 60}m`
                        : '-'}
                      {minutes > 0 && (
                        <button
                          className="edit-btn"
                          onClick={(e) => startEdit(e, doctor.doctorId, day, log)}
                          disabled={!!editing}
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  );
                })}

                <div className="row-cell total-time worked">
                  {Math.floor(weeklyTotalMinutes / 60)}h {weeklyTotalMinutes % 60}m
                </div>
              </div>

              {expandedCell.doctorId === doctor.doctorId && expandedCell.day && (
                <div className="timecard-row day-detail-row">
                  <div className="row-cell doctor-name day-label">
                    Details – {displayName} ({expandedCell.day})
                  </div>
                  <div className="row-cell day-detail-content" colSpan={daysOfWeek.length + 1}>
                    {(() => {
                      const log = weeklyLog[expandedCell.day] || {};
                      const breaks = Array.isArray(log.breaks) ? log.breaks : [];

                      const nearestIn =
                        log.clockInLat != null && log.clockInLng != null
                          ? getNearestOffice(log.clockInLat, log.clockInLng)
                          : null;
                      const nearestOut =
                        log.clockOutLat != null && log.clockOutLng != null
                          ? getNearestOffice(log.clockOutLat, log.clockOutLng)
                          : null;

                      return (
                        <div className="day-detail">
                          <div>
                            <strong>Clock In:</strong> {formatTime(log.clockIn)}
                          </div>
                          {log.clockInLat != null && log.clockInLng != null && (
                            <div>
                              <strong>Clock In Location:</strong>{' '}
                              {log.clockInLat.toFixed(6)}, {log.clockInLng.toFixed(6)}{' '}
                              {nearestIn && (
                                <span
                                  className={`badge ${getDistanceClass(
                                    nearestIn.distance
                                  )}`}
                                >
                                  {formatDistanceLabel(nearestIn.distance)} from{' '}
                                  {nearestIn.office?.name || 'nearest office'}
                                </span>
                              )}
                            </div>
                          )}

                          <div>
                            <strong>Clock Out:</strong>{' '}
                            {log.clockOut ? formatTime(log.clockOut) : 'N/A'}
                          </div>
                          {log.clockOutLat != null && log.clockOutLng != null && (
                            <div>
                              <strong>Clock Out Location:</strong>{' '}
                              {log.clockOutLat.toFixed(6)}, {log.clockOutLng.toFixed(6)}{' '}
                              {nearestOut && (
                                <span
                                  className={`badge ${getDistanceClass(
                                    nearestOut.distance
                                  )}`}
                                >
                                  {formatDistanceLabel(nearestOut.distance)} from{' '}
                                  {nearestOut.office?.name || 'nearest office'}
                                </span>
                              )}
                            </div>
                          )}

                          <div>
                            <strong>Total Worked:</strong>{' '}
                            {log.totalMinutes || 0} minutes
                          </div>

                          {breaks.length > 0 && (
                            <div className="breaks-list">
                              <strong>Breaks:</strong>
                              <ul>
                                {breaks.map((b, i) => (
                                  <li key={i}>
                                    {formatTime(b.breakStart)} →{' '}
                                    {b.breakEnd ? formatTime(b.breakEnd) : 'Ongoing'}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {breaks.length === 0 && (
                            <div>
                              <em>No breaks recorded.</em>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}

              {expandedDoctorId === doctor.doctorId && (
                <div className="expand-section">
                  <h4>All Days for {displayName}</h4>
                  {Object.entries(weeklyLog).map(([day, log]) => (
                    <div key={day} className="day-detail">
                      <strong>{day}:</strong>{' '}
                      Clock In: {formatTime(log.clockIn)}, Clock Out:{' '}
                      {log.clockOut ? formatTime(log.clockOut) : 'N/A'}, Total:{' '}
                      {log.totalMinutes || 0} min
                    </div>
                  ))}
                </div>
              )}
            </React.Fragment>
          );
        })}

        <div className="timecard-row summary-row">
          <div className="row-cell doctor-name">Total per Day</div>
          {daysOfWeek.map((day) => (
            <div key={day} className="row-cell total-time">
              {dailyTotals[day]
                ? `${Math.floor(dailyTotals[day] / 60)}h ${
                    dailyTotals[day] % 60
                  }m`
                : '-'}
            </div>
          ))}
          <div className="row-cell total-time" />
        </div>
      </div>
    </div>
  );
};

export default ManagerClockTimecard;
